<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            {{ translate("admin_schedules_list.sharing_schedule_title") }}
        </template>

        <template v-slot:body>
            <div class="field">
                <label class="label">{{ translate("admin_schedules_list.sharing_type") }}</label>
                <div class="select is-fullwidth">
                    <select v-model="sharingValue">
                        <option v-for="sharingType in sharingTypes" :value="sharingType.value" :key="sharingType.value">
                            {{ sharingType.text }}
                        </option>
                    </select>
                </div>
            </div>

            <div class="field" v-if="sharingValue === 2">
                <div class="control">
                    <Checkbox2 v-model="setAsPlanned">
                        {{ translate("admin_schedules_list.set_as_planned") }}
                    </Checkbox2>
                </div>
            </div>

            <div>
                <span>{{ translate("admin_schedules_list.if_not_sure_about_sharing") }} </span>
                <a :href="urls.helpCenter().sharingSchedule()">{{ translate("common.read_more") }}</a>
            </div>

            <div v-if="sharingValue === 1" class="mt-3 has-text-warning">
                <span class="icon"><i class="fa fa-exclamation-triangle" /></span>
                {{ translate("admin_schedules_list.static_warning") }}
            </div>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button @click="close" class="button">
                    {{ translate("common.cancel") }}
                </button>
                <LoadingButton :is-loading="isLoading" @click="confirm" class="button is-primary">
                    {{ translate("common.ok") }}
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { useModal } from "../Common/useModal";
    import RegularModal from "../Common/RegularModal.vue";
    import Checkbox2 from "../Common/Checkbox2.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import urls from "../Urls/urls";
    import { successToast } from "../Common/toasts";
    import { Schedule, ScheduleSharing } from "../Common/Schedule/types";
    import { useSchedulesListingStore, SharingTypeOption } from "./useSchedulesListingStore";

    const props = defineProps<{
        sharingTypes: SharingTypeOption[];
    }>();

    const emit = defineEmits<{
        offerFightsQueueSharing: [];
    }>();

    const store = useSchedulesListingStore();

    const { modal, open: openModal, close: closeModal } = useModal();

    const isLoading = ref(false);
    const scheduleId = ref(0);
    const sharingValue = ref(0);
    const setAsPlanned = ref(true);

    function confirm() {
        isLoading.value = true;

        return store
            .updateScheduleSharing(scheduleId.value, sharingValue.value, setAsPlanned.value)
            .then(() => {
                successToast(translate("common.changes_have_been_saved"));
                close();

                if (shouldOfferFightsQueueSharing()) {
                    emit("offerFightsQueueSharing");
                }
            })
            .catch(handleError)
            .finally(() => {
                isLoading.value = false;
            });
    }

    // The public fights queue is built from the active schedule only, and "Based on fights" is where the organizer
    // publishes fight-derived data (also in real time), so that is when to ask.
    function shouldOfferFightsQueueSharing() {
        const publishesFights = sharingValue.value === ScheduleSharing.FightsOrder || sharingValue.value === ScheduleSharing.FightsRealTime;
        return publishesFights && scheduleId.value === store.activeSchedule.id && store.sharePublicFightsQueue === false;
    }

    function open(schedule: Schedule) {
        scheduleId.value = schedule.id;
        sharingValue.value = schedule.sharing;
        openModal();
    }

    function close() {
        closeModal();
    }

    defineExpose({ open });
</script>
